import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../environments/environment';
import { RetryService } from './services/retry.service';

@Injectable({
  providedIn: 'root',
})
export class AzureOpenaiService {
  // All API calls now go through backend proxy - no API keys in frontend
  private apiBaseUrl = environment.apiBaseUrl;
  
  systemMessage = `This is an assistant based on HarperCollins author contract information..
    For this to be an effective assistant, it needs to use the content loaded as knowledge, but if an answer requires more general information, it's critical that it be cited and clearly identified as content that was collected from the web.  This assistant is designed to be an expert, so respond with authoritative answer and if any of the response is based on data from outside of Knowledge, then clearly explain that. Since some of these topics are complex, it's OK to ask follow up questions to the user to gain further clarification on the question to best answer it.
    Make sure For every answer, provide a reference to one of contracts in Knowledge and also chunk of document as a link.
    
    For statistic questions or listing questions, referring to ContractCatalogue.docx for full catalogue information. But do not refer to the file name in the response.
    Do not make up answer or "guess" at what they are asking.
    
    In your response, identify if the content is coming from a contract.
    keep the responses short and to the point. Offer to provide more information if the user wants more, but keep the answer sharp and to the point.
    `;

  constructor(private retryService: RetryService) { }

  getStreamingResponseNew(context: any, args: any): Promise<ReadableStreamDefaultReader<Uint8Array> | undefined> {
    // Use backend proxy endpoint - API key stays server-side
    const url = `${this.apiBaseUrl}/api/v1/azure/chat/completions?db_name=v2`;
    
    const headers = new Headers({
      'Content-Type': 'application/json',
    });
    
    const body = JSON.stringify({
      model: 'gpt4-o',
      messages: [
        { role: 'system', content: `This is an assistant based on HarperCollins author contract information...` },
        { role: 'user', content: "Hi" }
      ],
      temperature: 0.1,
      stream: true,
      max_tokens: 2000,
    });
    
    return fetch(url, {
      method: 'POST',
      headers,
      body,
    })
      .then((response) => response.body?.getReader())
      .catch((error) => {        return undefined;
      });
  }

  // Updated function to handle context-based chat with entire conversation history
  // Now routes through secure backend proxy - no API keys exposed in browser
  // Includes automatic retry logic (up to 3 attempts)
  getStreamingResponse({ messages }: any, model: string, temperature: any, reasoning_effort: any, user_id?: string, session_id?: string): Promise<ReadableStreamDefaultReader<Uint8Array> | undefined> {
    // Determine the correct backend proxy endpoint based on model
    let url: string;
    
    if (model === 'grok-2-latest') {
      // X.AI uses its own proxy endpoint
      url = `${this.apiBaseUrl}/api/v1/xai/chat/completions?db_name=v2`;
    } else {
      // All Azure OpenAI models use the azure proxy
      url = `${this.apiBaseUrl}/api/v1/azure/chat/completions?db_name=v2`;
    }
    
    const headers = new Headers({
      'Content-Type': 'application/json',
    });

    const isGpt5Mini = model === 'gpt-5-mini';
    const req: any = {
      model: model,
      messages: messages,
      stream: true,
      user_id: user_id,
      session_id: session_id
    };
    
    // GPT-5 Mini only supports default temperature=1; other models use provided numeric
    if (isGpt5Mini) {
      req.temperature = 1;
      // Increase max_completion_tokens for high reasoning to allow longer, more thorough responses
      if (reasoning_effort === 'high') {
        // Allow extra room to reduce premature cutoff for high reasoning traces
        req.max_completion_tokens = 12000;
      } else if (reasoning_effort === 'medium') {
        req.max_completion_tokens = 8000;
      } else {
        req.max_completion_tokens = 4000;
      }
      req.response_format = { type: 'text' };
      req.reasoning_effort = reasoning_effort;
    } else {
      req.temperature = parseFloat(temperature);
      req.max_tokens = 2000;
    }
    
    const body = JSON.stringify(req);
    
    // Create a function that makes the API call
    const makeApiCall = async (): Promise<ReadableStreamDefaultReader<Uint8Array> | undefined> => {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body,
      });
      
      if (!response.ok) {
        try {
          const err = await response.json();
          throw err;
        } catch (e) {
          throw new Error(`HTTP ${response.status}`);
        }
      }
      
      return response.body?.getReader();
    };
    
    // Use retry service for automatic retries (up to 3 attempts with exponential backoff)
    return this.retryService.retryStream(makeApiCall, {
      maxAttempts: 3,
      initialDelay: 1000,
      maxDelay: 10000,
      backoffMultiplier: 2
    }).catch((error) => {     // Return undefined to allow error handling in the component
      return undefined;
    });
  }

  getstream(context: string) { }

  /**
   * Gemini streaming via backend proxy - API key stays server-side
   */
  getGeminiStreamingResponse(
    messages: any[],
    model: string,
    temperature: number,
    systemPrompt?: string
  ): Promise<ReadableStreamDefaultReader<Uint8Array> | undefined> {
    const url = `${this.apiBaseUrl}/api/v1/azure/gemini/generate-content?db_name=v2`;
    
    const headers = new Headers({
      'Content-Type': 'application/json',
    });

    // Convert messages to Gemini format
    const contents = messages.map(msg => ({
      role: msg.role === 'assistant' ? 'model' : msg.role,
      parts: [{ text: msg.content }]
    }));

    // Add system instruction if provided
    if (systemPrompt) {
      contents.unshift({
        role: 'user',
        parts: [{ text: `System: ${systemPrompt}` }]
      });
    }

    const req = {
      model: model,
      contents: contents,
      stream: true,
      generationConfig: {
        temperature: temperature,
        responseMimeType: 'text/plain',
      }
    };

    const body = JSON.stringify(req);

    return fetch(url, {
      method: 'POST',
      headers,
      body,
    })
      .then(async (response) => {
        if (!response.ok) {
          try {
            const err = await response.json();
            throw err;
          } catch (e) {
            throw new Error(`HTTP ${response.status}`);
          }
        }
        return response.body?.getReader();
      })
      .catch((error) => {        return undefined;
      });
  }
}
